import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api } from '../api/client';
import type { SuperadminPromo, PromoFundedBy, PromoWindow } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { useModal } from '../context/ModalContext';
import {
  DOW_LABELS,
  WEEKDAYS_MASK,
  formatPromoWindows,
  indicesFromDowMask,
  dowMaskFromIndices,
} from '../utils/promoWindows';

interface WindowDraft {
  dowIndices: number[];
  localStartTime: string;
  localEndTime: string;
}

function defaultWindowDraft(): WindowDraft {
  return {
    dowIndices: indicesFromDowMask(WEEKDAYS_MASK),
    localStartTime: '20:00',
    localEndTime: '22:00',
  };
}

function draftToPayload(draft: WindowDraft): PromoWindow | null {
  if (!draft.dowIndices.length || !draft.localStartTime || !draft.localEndTime) return null;
  if (draft.localEndTime <= draft.localStartTime) return null;
  return {
    dowMask: dowMaskFromIndices(draft.dowIndices),
    localStartTime: draft.localStartTime,
    localEndTime: draft.localEndTime,
  };
}

interface VenueOption {
  id: string;
  name: string;
  city: string;
  state: string;
}

const discountPresets = [100, 50, 25];

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid rgba(15, 23, 42, 0.08)',
  borderRadius: '20px',
  boxShadow: '0 20px 45px rgba(15, 23, 42, 0.06)',
};

export default function PromoCodesScreen() {
  const { isSuperAdmin } = useAuth();
  const { showAlert } = useModal();
  const selectedVenueId = api.getSelectedVenueId();

  const [venues, setVenues] = useState<VenueOption[]>([]);
  const [selectedFilterVenueId, setSelectedFilterVenueId] = useState<string>('');
  const [pageError, setPageError] = useState<string | null>(null);
  const [loadingVenues, setLoadingVenues] = useState(true);
  const [loadingPromos, setLoadingPromos] = useState(true);
  const [savingPromo, setSavingPromo] = useState(false);
  const [promos, setPromos] = useState<SuperadminPromo[]>([]);
  const [form, setForm] = useState({
    venueId: '',
    percentOff: 100,
    code: '',
    maxRedemptionsTotal: '',
    maxRedemptionsPerDay: '',
    expiresAt: '',
    fundedBy: 'venue' as PromoFundedBy,
    maxDiscountCentsPerRedemption: '',
    maxDiscountCentsTotal: '',
  });
  const [windowDrafts, setWindowDrafts] = useState<WindowDraft[]>([]);
  const [windowsEnabled, setWindowsEnabled] = useState(false);

  const loadVenues = useCallback(async () => {
    try {
      setLoadingVenues(true);
      setPageError(null);

      // Load every venue in a single request via the lightweight options
      // endpoint. Paging through getSuperadminVenues used to take dozens of
      // sequential requests (the catalog is thousands of venues), which
      // tripped Cloudflare's per-IP edge rate limit mid-loop and surfaced
      // as CORS failures.
      const res = await api.getSuperadminVenueOptions();
      const nextVenues = res.venues
        .map((venue) => ({
          id: venue.id,
          name: venue.name,
          city: venue.city,
          state: venue.state,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setVenues(nextVenues);

      const fallbackVenueId = selectedVenueId || nextVenues[0]?.id || '';
      setSelectedFilterVenueId((current) => current || fallbackVenueId);
      setForm((current) => ({
        ...current,
        venueId: current.venueId || fallbackVenueId,
      }));
    } catch (err) {
      setPageError(err instanceof Error ? err.message : 'Failed to load venues');
    } finally {
      setLoadingVenues(false);
    }
  }, [selectedVenueId]);

  const loadPromos = useCallback(async (venueId?: string) => {
    try {
      setLoadingPromos(true);
      setPageError(null);
      const response = await api.getSuperadminPromos(venueId || undefined);
      setPromos(response.promos);
    } catch (err) {
      setPromos([]);
      const msg = err instanceof Error ? err.message : 'Failed to load promo codes';
      // Suppress 404 — the endpoint may not be deployed yet
      if (msg.toLowerCase().includes('not found')) return;
      setPageError(msg);
    } finally {
      setLoadingPromos(false);
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadVenues();
  }, [isSuperAdmin, loadVenues]);

  useEffect(() => {
    if (!isSuperAdmin || !selectedFilterVenueId) return;
    loadPromos(selectedFilterVenueId);
  }, [isSuperAdmin, selectedFilterVenueId, loadPromos]);

  const selectedVenue = useMemo(
    () => venues.find((venue) => venue.id === form.venueId) || null,
    [venues, form.venueId]
  );

  const readyCount = promos.filter((promo) => promo.active).length;

  const handleCreatePromo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.venueId) {
      await showAlert('Venue Required', 'Choose a venue before creating a promo code.', 'error');
      return;
    }

    const dollarsToCents = (raw: string): number | null => {
      const trimmed = raw.trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed <= 0) return null;
      return Math.round(parsed * 100);
    };

    const windowsPayload: PromoWindow[] = [];
    if (windowsEnabled) {
      if (windowDrafts.length === 0) {
        await showAlert('Window Required', 'Add at least one time window or disable the restriction.', 'error');
        return;
      }
      for (let i = 0; i < windowDrafts.length; i += 1) {
        const payload = draftToPayload(windowDrafts[i]);
        if (!payload) {
          await showAlert(
            'Invalid Window',
            `Window ${i + 1}: pick at least one day and ensure end time is after start time.`,
            'error'
          );
          return;
        }
        windowsPayload.push(payload);
      }
    }

    try {
      setSavingPromo(true);
      await api.createSuperadminPromo({
        venueId: form.venueId,
        percentOff: form.percentOff,
        code: form.code.trim() || undefined,
        maxRedemptionsTotal: form.maxRedemptionsTotal ? Number(form.maxRedemptionsTotal) : null,
        maxRedemptionsPerCustomer: 1,
        maxRedemptionsPerDay: form.maxRedemptionsPerDay ? Number(form.maxRedemptionsPerDay) : null,
        expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
        fundedBy: form.fundedBy,
        maxDiscountCentsPerRedemption: dollarsToCents(form.maxDiscountCentsPerRedemption),
        maxDiscountCentsTotal: dollarsToCents(form.maxDiscountCentsTotal),
        windows: windowsPayload,
      });
      setForm((current) => ({
        ...current,
        code: '',
        maxRedemptionsTotal: '',
        maxRedemptionsPerDay: '',
        expiresAt: '',
        maxDiscountCentsPerRedemption: '',
        maxDiscountCentsTotal: '',
      }));
      setWindowsEnabled(false);
      setWindowDrafts([]);
      setSelectedFilterVenueId(form.venueId);
      await loadPromos(form.venueId);
      await showAlert('Promo Created', 'The promo code is live and ready to redeem.', 'success');
    } catch (err) {
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to create promo code', 'error');
    } finally {
      setSavingPromo(false);
    }
  };

  const updateWindowDraft = (index: number, patch: Partial<WindowDraft>) => {
    setWindowDrafts((current) => current.map((draft, i) => (i === index ? { ...draft, ...patch } : draft)));
  };

  const toggleWindowDow = (index: number, dowIndex: number) => {
    setWindowDrafts((current) => current.map((draft, i) => {
      if (i !== index) return draft;
      const has = draft.dowIndices.includes(dowIndex);
      const next = has
        ? draft.dowIndices.filter((d) => d !== dowIndex)
        : [...draft.dowIndices, dowIndex].sort();
      return { ...draft, dowIndices: next };
    }));
  };

  const addWindowDraft = () => {
    setWindowDrafts((current) => [...current, defaultWindowDraft()]);
  };

  const removeWindowDraft = (index: number) => {
    setWindowDrafts((current) => current.filter((_, i) => i !== index));
  };

  const handleToggleWindowsEnabled = () => {
    setWindowsEnabled((current) => {
      const next = !current;
      if (next && windowDrafts.length === 0) {
        setWindowDrafts([defaultWindowDraft()]);
      }
      return next;
    });
  };

  const handleTogglePromo = async (promo: SuperadminPromo) => {
    try {
      setLoadingPromos(true);
      await api.setSuperadminPromoActive(promo.id, !promo.active);
      await loadPromos(selectedFilterVenueId);
    } catch (err) {
      setLoadingPromos(false);
      await showAlert('Error', err instanceof Error ? err.message : 'Failed to update promo', 'error');
    }
  };

  if (!isSuperAdmin) {
    return (
      <DashboardLayout>
        <div className="page-header">
          <h1>Access Denied</h1>
          <p>This page is only available to Super Administrators.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header" style={{ marginBottom: '28px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1>Promo Codes</h1>
          <p>Simple venue-level promo creation and inventory.</p>
        </div>
        <a className="btn btn-secondary" href="/superadmin/promos/analytics" style={{ textDecoration: 'none' }}>
          Analytics &amp; safeguards →
        </a>
      </div>

      {pageError && (
        <div
          style={{
            marginBottom: '20px',
            padding: '14px 16px',
            borderRadius: '14px',
            border: '1px solid rgba(185, 28, 28, 0.12)',
            background: 'rgba(254, 242, 242, 0.92)',
            color: '#991b1b',
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {pageError}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 400px) minmax(0, 1fr)', gap: '24px', alignItems: 'start' }}>
        <section
          className="card"
          style={{
            ...cardStyle,
            padding: '28px',
            background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
            position: 'sticky',
            top: '24px',
          }}
        >
          <div style={{ display: 'grid', gap: '10px' }}>
            <span style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--gray-500)' }}>
              Create Promo
            </span>
            <h2 style={{ margin: 0, fontSize: '30px', lineHeight: 1.05, letterSpacing: '-0.03em' }}>Lean by default</h2>
            <p style={{ margin: 0, color: 'var(--gray-600)', lineHeight: 1.5, maxWidth: '32ch' }}>
              Pick a venue, choose the offer, and create the code. The account limit stays one per user.
            </p>
          </div>

          <form onSubmit={handleCreatePromo} style={{ display: 'grid', gap: '20px', marginTop: '28px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: 'var(--gray-700)' }}>
                Venue
              </label>
              <select
                value={form.venueId}
                onChange={(e) => setForm((current) => ({ ...current, venueId: e.target.value }))}
                disabled={loadingVenues}
                style={{ width: '100%' }}
              >
                <option value="">Select a venue</option>
                {venues.map((venue) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '10px', color: 'var(--gray-700)' }}>
                Discount
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '10px' }}>
                {discountPresets.map((preset) => {
                  const active = form.percentOff === preset;
                  return (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setForm((current) => ({ ...current, percentOff: preset }))}
                      style={{
                        borderRadius: '14px',
                        border: active ? '1px solid #1d4ed8' : '1px solid rgba(15, 23, 42, 0.12)',
                        background: active ? 'rgba(29, 78, 216, 0.08)' : '#fff',
                        color: active ? '#1d4ed8' : 'var(--gray-700)',
                        padding: '14px 12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                    >
                      {preset}% off
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '10px', color: 'var(--gray-700)' }}>
                Funded by
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                {(['venue', 'platform'] as PromoFundedBy[]).map((option) => {
                  const active = form.fundedBy === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setForm((current) => ({ ...current, fundedBy: option }))}
                      style={{
                        borderRadius: '14px',
                        border: active ? '1px solid #1d4ed8' : '1px solid rgba(15, 23, 42, 0.12)',
                        background: active ? 'rgba(29, 78, 216, 0.08)' : '#fff',
                        color: active ? '#1d4ed8' : 'var(--gray-700)',
                        padding: '14px 12px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        textAlign: 'center',
                      }}
                    >
                      {option === 'venue' ? 'Venue pays' : 'PickleCourts pays'}
                    </button>
                  );
                })}
              </div>
              <p style={{ marginTop: '8px', color: 'var(--gray-500)', fontSize: '12px', lineHeight: 1.4 }}>
                {form.fundedBy === 'venue'
                  ? 'The venue absorbs the discount via reduced payout.'
                  : 'PickleCourts covers the discount; venue keeps their normal cut. Anything past our platform fee is tracked as a pending reimbursement.'}
              </p>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: 'var(--gray-700)' }}>
                Dollar caps
                <span style={{ fontWeight: 500, color: 'var(--gray-500)', marginLeft: '6px' }}>(optional)</span>
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                <div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.maxDiscountCentsPerRedemption}
                    onChange={(e) => setForm((current) => ({ ...current, maxDiscountCentsPerRedemption: e.target.value }))}
                    placeholder="Max $/redemption"
                  />
                </div>
                <div>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.maxDiscountCentsTotal}
                    onChange={(e) => setForm((current) => ({ ...current, maxDiscountCentsTotal: e.target.value }))}
                    placeholder="Lifetime budget $"
                  />
                </div>
              </div>
              <p style={{ marginTop: '6px', color: 'var(--gray-500)', fontSize: '12px', lineHeight: 1.4 }}>
                Hard caps in dollars. Useful for 100%-off codes so one booking can't drain the budget.
              </p>
            </div>

            <div style={{
              borderRadius: '18px',
              padding: '18px',
              background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.04), rgba(255, 255, 255, 0.88))',
              border: '1px solid rgba(15, 23, 42, 0.07)',
              display: 'grid',
              gap: '6px',
            }}>
              <span style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--gray-500)' }}>
                Preview
              </span>
              <strong style={{ fontSize: '20px', lineHeight: 1.2 }}>
                {form.percentOff === 100 ? 'Free booking promo' : `${form.percentOff}% off booking promo`}
              </strong>
              <span style={{ color: 'var(--gray-600)', lineHeight: 1.45 }}>
                {selectedVenue ? `${selectedVenue.name}${selectedVenue.city ? `, ${selectedVenue.city}` : ''}` : 'Choose a venue to finish setup'}
              </span>
              <span style={{ color: 'var(--gray-500)', fontSize: '12px' }}>
                {form.fundedBy === 'venue' ? 'Venue pays' : 'PickleCourts pays'}
              </span>
              {windowsEnabled && windowDrafts.length > 0 && (
                <span style={{ color: 'var(--gray-600)', fontSize: '13px' }}>
                  Valid {formatPromoWindows(
                    windowDrafts
                      .map((d) => draftToPayload(d))
                      .filter((p): p is PromoWindow => p !== null)
                  )}
                </span>
              )}
            </div>

            <div style={{
              borderRadius: '18px',
              padding: '18px',
              border: '1px solid rgba(15, 23, 42, 0.07)',
              background: '#fff',
              display: 'grid',
              gap: '14px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--gray-700)' }}>Time-of-day restrictions</div>
                  <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '2px' }}>
                    Limit when this promo applies (e.g. weekdays 8–10 PM or Sun 8–11 AM).
                  </div>
                </div>
                <label style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={windowsEnabled}
                    onChange={handleToggleWindowsEnabled}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                </label>
              </div>

              {windowsEnabled && (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {windowDrafts.map((draft, index) => (
                    <div
                      key={index}
                      style={{
                        border: '1px solid rgba(15, 23, 42, 0.10)',
                        borderRadius: '14px',
                        padding: '14px',
                        background: '#fcfcfd',
                        display: 'grid',
                        gap: '10px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gray-500)' }}>
                          Window {index + 1}
                        </span>
                        {windowDrafts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeWindowDraft(index)}
                            style={{ background: 'transparent', border: 'none', color: '#991b1b', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {DOW_LABELS.map((label, i) => {
                          const active = draft.dowIndices.includes(i);
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => toggleWindowDow(index, i)}
                              style={{
                                borderRadius: '999px',
                                border: active ? '1px solid #1d4ed8' : '1px solid rgba(15, 23, 42, 0.12)',
                                background: active ? 'rgba(29, 78, 216, 0.08)' : '#fff',
                                color: active ? '#1d4ed8' : 'var(--gray-700)',
                                padding: '6px 12px',
                                fontWeight: 600,
                                fontSize: '12px',
                                cursor: 'pointer',
                              }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: 'var(--gray-700)' }}>Start (venue local)</label>
                          <input
                            type="time"
                            value={draft.localStartTime}
                            onChange={(e) => updateWindowDraft(index, { localStartTime: e.target.value })}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '11px', fontWeight: 700, marginBottom: '4px', color: 'var(--gray-700)' }}>End (venue local)</label>
                          <input
                            type="time"
                            value={draft.localEndTime}
                            onChange={(e) => updateWindowDraft(index, { localEndTime: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addWindowDraft}
                    style={{
                      borderRadius: '12px',
                      border: '1px dashed rgba(15, 23, 42, 0.18)',
                      background: '#fff',
                      padding: '10px 14px',
                      fontWeight: 600,
                      color: 'var(--gray-700)',
                      cursor: 'pointer',
                    }}
                  >
                    + Add another window
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: 'var(--gray-700)' }}>
                  Promo code
                </label>
                <input
                  value={form.code}
                  onChange={(e) => setForm((current) => ({ ...current, code: e.target.value.toUpperCase() }))}
                  placeholder="Auto-generate if blank"
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: 'var(--gray-700)' }}>
                    Total uses
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.maxRedemptionsTotal}
                    onChange={(e) => setForm((current) => ({ ...current, maxRedemptionsTotal: e.target.value }))}
                    placeholder="Unlimited"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: 'var(--gray-700)' }}>
                    Per day
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.maxRedemptionsPerDay}
                    onChange={(e) => setForm((current) => ({ ...current, maxRedemptionsPerDay: e.target.value }))}
                    placeholder="No limit"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: 'var(--gray-700)' }}>
                    Expiry
                  </label>
                  <input
                    type="date"
                    value={form.expiresAt}
                    onChange={(e) => setForm((current) => ({ ...current, expiresAt: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <button className="btn btn-primary" type="submit" disabled={savingPromo || !form.venueId} style={{ width: '100%' }}>
              {savingPromo ? 'Creating...' : 'Create Promo Code'}
            </button>
          </form>
        </section>

        <section
          className="card"
          style={{
            ...cardStyle,
            padding: '28px',
            background: '#fcfcfd',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ display: 'grid', gap: '6px' }}>
              <span style={{ fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, color: 'var(--gray-500)' }}>
                Existing Codes
              </span>
              <h2 style={{ margin: 0, fontSize: '30px', lineHeight: 1.05, letterSpacing: '-0.03em' }}>Promo inventory</h2>
              <p style={{ margin: 0, color: 'var(--gray-600)' }}>
                {readyCount} active {readyCount === 1 ? 'code' : 'codes'} for this venue.
              </p>
            </div>
            <div style={{ minWidth: '280px', width: 'min(100%, 320px)' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '8px', color: 'var(--gray-700)' }}>
                Viewing venue
              </label>
              <select
                value={selectedFilterVenueId}
                onChange={(e) => setSelectedFilterVenueId(e.target.value)}
                disabled={loadingVenues}
                style={{ width: '100%' }}
              >
                <option value="">Select a venue</option>
                {venues.map((venue) => (
                  <option key={venue.id} value={venue.id}>
                    {venue.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginTop: '24px', display: 'grid', gap: '14px' }}>
            {loadingPromos ? (
              <div className="loading" style={{ minHeight: '180px' }}><div className="spinner"></div></div>
            ) : promos.length === 0 ? (
              <div style={{
                border: '1px dashed rgba(15, 23, 42, 0.12)',
                borderRadius: '18px',
                padding: '32px',
                color: 'var(--gray-600)',
                background: '#ffffff',
                textAlign: 'center',
              }}>
                No promo codes yet for this venue.
              </div>
            ) : (
              promos.map((promo) => (
                <article
                  key={promo.id}
                  style={{
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    borderRadius: '18px',
                    padding: '18px 20px',
                    background: promo.active ? '#fff' : 'rgba(248, 250, 252, 0.92)',
                    display: 'grid',
                    gap: '14px',
                    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ display: 'grid', gap: '6px' }}>
                      <strong style={{ fontSize: '18px', letterSpacing: '0.02em' }}>{promo.code}</strong>
                      <span style={{ color: 'var(--gray-600)' }}>
                        {promo.percentOff === 100 ? 'Free booking' : `${promo.percentOff}% off`} at {promo.venueName || 'selected venue'}
                      </span>
                      {promo.windows && promo.windows.length > 0 && (
                        <span style={{ color: 'var(--gray-500)', fontSize: '12px' }}>
                          Valid {formatPromoWindows(promo.windows)}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                      <span style={{
                        alignSelf: 'flex-end',
                        borderRadius: '999px',
                        padding: '6px 10px',
                        fontSize: '12px',
                        fontWeight: 700,
                        background: promo.active ? 'rgba(22, 163, 74, 0.10)' : 'rgba(100, 116, 139, 0.10)',
                        color: promo.active ? '#15803d' : '#475569',
                      }}>
                        {promo.active ? 'Active' : 'Inactive'}
                      </span>
                      <span style={{
                        borderRadius: '999px',
                        padding: '4px 10px',
                        fontSize: '11px',
                        fontWeight: 700,
                        background: promo.fundedBy === 'platform' ? 'rgba(29, 78, 216, 0.10)' : 'rgba(100, 116, 139, 0.10)',
                        color: promo.fundedBy === 'platform' ? '#1d4ed8' : '#475569',
                      }}>
                        {promo.fundedBy === 'platform' ? 'PickleCourts pays' : 'Venue pays'}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gray-500)' }}>Redemptions</div>
                      <div style={{ marginTop: '4px', fontWeight: 700 }}>{promo.redemptionCount}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gray-500)' }}>Used</div>
                      <div style={{ marginTop: '4px', fontWeight: 700 }}>{promo.consumedCount}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gray-500)' }}>Expires</div>
                      <div style={{ marginTop: '4px', fontWeight: 700 }}>{promo.expiresAt ? new Date(promo.expiresAt).toLocaleDateString() : 'No expiry'}</div>
                    </div>
                  </div>

                  {(promo.maxDiscountCentsTotal != null
                    || promo.maxDiscountCentsPerRedemption != null
                    || promo.platformAbsorbedCents > 0
                    || promo.pendingVenueReimbursementCents > 0) && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px', borderTop: '1px dashed rgba(15, 23, 42, 0.08)', paddingTop: '12px' }}>
                      {promo.maxDiscountCentsTotal != null && (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gray-500)' }}>Budget used</div>
                          <div style={{ marginTop: '4px', fontWeight: 700 }}>
                            ${(promo.totalDiscountCentsUsed / 100).toFixed(2)} / ${(promo.maxDiscountCentsTotal / 100).toFixed(2)}
                          </div>
                        </div>
                      )}
                      {promo.maxDiscountCentsPerRedemption != null && (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gray-500)' }}>Max per use</div>
                          <div style={{ marginTop: '4px', fontWeight: 700 }}>
                            ${(promo.maxDiscountCentsPerRedemption / 100).toFixed(2)}
                          </div>
                        </div>
                      )}
                      {promo.platformAbsorbedCents > 0 && (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--gray-500)' }}>Platform absorbed</div>
                          <div style={{ marginTop: '4px', fontWeight: 700 }}>
                            ${(promo.platformAbsorbedCents / 100).toFixed(2)}
                          </div>
                        </div>
                      )}
                      {promo.pendingVenueReimbursementCents > 0 && (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#b45309' }}>Owed to venue</div>
                          <div style={{ marginTop: '4px', fontWeight: 700, color: '#b45309' }}>
                            ${(promo.pendingVenueReimbursementCents / 100).toFixed(2)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--gray-500)', fontSize: '13px' }}>
                      {promo.maxRedemptionsTotal ? `${promo.availableRedemptionCount} remaining` : 'Unlimited total uses'}
                    </span>
                    <button className="btn btn-secondary" onClick={() => handleTogglePromo(promo)}>
                      {promo.active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
